// ITR JSON importer.
// The Income Tax e-filing portal lets a taxpayer download a filled
// ITR-1/2/3/4 JSON. The schema is documented under "ITR Schema v1.1" for
// AY 2025-26 / FY 2024-25. We only parse the slices Wasiyat needs:
//
//   - Identity (name, PAN, dob)
//   - Demat capital gains schedule (for crypto / equity asset hints)
//   - Schedule AL (assets + liabilities, for >₹50L income filers)
//   - Tax-paid summary
//
// Wasiyat uses Schedule AL as a goldmine to auto-populate its asset vault
// with bank accounts, immovable property, jewellery, and vehicles that
// the user already declared on their return.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok, shortHash } from './_types.ts';

export type ItrFormCode = 'ITR1' | 'ITR2' | 'ITR3' | 'ITR4';

export interface ItrIdentity {
  readonly nameFull: string;
  readonly pan: string;
  readonly dob: string;
  readonly assessmentYear: string;
  readonly formCode: ItrFormCode;
  readonly residentialStatus: string;
}

export interface ItrScheduleAlRow {
  readonly category: 'immovable' | 'movable' | 'bank' | 'shares' | 'insurance' | 'jewellery' | 'vehicle' | 'cash' | 'other';
  readonly description: string;
  readonly costPaise: number;
}

export interface ItrCapitalGainRow {
  readonly type: 'stcg-equity' | 'ltcg-equity' | 'stcg-other' | 'ltcg-other' | 'crypto-vda';
  readonly grossPaise: number;
  readonly costPaise: number;
  readonly netPaise: number;
}

export interface ItrTaxPaid {
  readonly tdsPaise: number;
  readonly advanceTaxPaise: number;
  readonly selfAssessmentTaxPaise: number;
  readonly refundClaimedPaise: number;
}

export interface ItrSnapshot {
  readonly identity: ItrIdentity;
  readonly scheduleAl: readonly ItrScheduleAlRow[];
  readonly capitalGains: readonly ItrCapitalGainRow[];
  readonly taxPaid: ItrTaxPaid;
  readonly grossTotalIncomePaise: number;
  readonly fetchedAt: string;
}

export interface ItrConnectorStatus extends ConnectorStatus {
  readonly id: 'itr-importer';
  readonly latestAssessmentYear: string | null;
}

interface ItrPersist {
  readonly fetchedAt: string;
  readonly panHash: string;
  readonly assessmentYear: string;
  readonly formCode: ItrFormCode;
  readonly grossTotalIncomePaise: number;
  readonly scheduleAlRows: number;
}

const STORE_KEY = 'empire/connectors/itr-importer';

export class ItrImporterAdapter
  implements ConnectorAdapter<ItrConnectorStatus, ItrSnapshot>
{
  readonly id = 'itr-importer';
  readonly displayName = 'ITR JSON importer';
  readonly displayNameHi = 'आईटीआर जेएसओएन आयात';
  readonly dpdpCategory = 'wealth-tax-return';
  readonly mode: ConnectorMode;
  #pendingJson: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<ItrConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.fetchedAt ?? null,
      principalIdHash: persisted?.panHash ?? null,
      latestAssessmentYear: persisted?.assessmentYear ?? null,
      notes: 'JSON downloaded from incometax.gov.in. AY 2024-25 + 2025-26 schema supported. Schedule AL parsed when present.',
    };
  }

  async connect(): Promise<ConnectorResult<ItrConnectorStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  queueJson(jsonText: string): void {
    this.#pendingJson = jsonText;
  }

  async importPayload(): Promise<ConnectorResult<ItrSnapshot>> {
    if (!this.#pendingJson) {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    try {
      const snap = parseItrJson(this.#pendingJson);
      if (!snap) {
        return err({ code: 'parse', message: 'ITR JSON did not match supported schema.', retriable: false });
      }
      await this.#persist(snap);
      return ok(snap);
    } catch (e) {
      return err({ code: 'parse', message: e instanceof Error ? e.message : 'parse-error', retriable: false });
    }
  }

  mockPayload(): ItrSnapshot {
    return {
      identity: {
        nameFull: 'Aditya Sharma',
        pan: 'ABCDE1234F',
        dob: '1992-04-15',
        assessmentYear: '2025-26',
        formCode: 'ITR2',
        residentialStatus: 'Resident',
      },
      scheduleAl: [
        { category: 'immovable', description: 'Residential flat · Friends Colony East, New Delhi', costPaise: 1_85_00_000_00 },
        { category: 'bank', description: 'HDFC Bank · A/C ****7891', costPaise: 8_25_000_00 },
        { category: 'shares', description: 'Zerodha demat · DP ****4422', costPaise: 22_40_000_00 },
        { category: 'jewellery', description: 'Mother\'s gold set · 240 grams 22-karat', costPaise: 12_50_000_00 },
        { category: 'vehicle', description: 'Honda City · DL3CBA1142', costPaise: 9_80_000_00 },
      ],
      capitalGains: [
        { type: 'ltcg-equity', grossPaise: 1_80_000_00, costPaise: 1_00_000_00, netPaise: 80_000_00 },
        { type: 'crypto-vda', grossPaise: 32_000_00, costPaise: 50_000_00, netPaise: -18_000_00 },
      ],
      taxPaid: { tdsPaise: 4_80_000_00, advanceTaxPaise: 0, selfAssessmentTaxPaise: 0, refundClaimedPaise: 14_220_00 },
      grossTotalIncomePaise: 36_00_000_00,
      fetchedAt: nowIso(),
    };
  }

  async #persist(snap: ItrSnapshot): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const rec: ItrPersist = {
      fetchedAt: snap.fetchedAt,
      panHash: await shortHash(snap.identity.pan),
      assessmentYear: snap.identity.assessmentYear,
      formCode: snap.identity.formCode,
      grossTotalIncomePaise: snap.grossTotalIncomePaise,
      scheduleAlRows: snap.scheduleAl.length,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): ItrPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as ItrPersist; } catch { return null; }
}

interface ItrJson {
  readonly ITR?: Record<string, unknown>;
}

export function parseItrJson(jsonText: string): ItrSnapshot | null {
  let parsed: ItrJson;
  try { parsed = JSON.parse(jsonText) as ItrJson; } catch { return null; }
  if (!parsed.ITR) return null;
  // ITR JSON wraps the form under "ITR.ITR1", "ITR.ITR2" etc.
  const formCode = (Object.keys(parsed.ITR)[0] ?? 'ITR2') as ItrFormCode;
  const form = (parsed.ITR as Record<string, Record<string, unknown>>)[formCode] ?? {};
  const personalInfo = (form['PersonalInfo'] ?? form['CreationInfo'] ?? {}) as Record<string, unknown>;
  const name = (personalInfo['AssesseeName'] as Record<string, unknown> | undefined)?.['FirstName'] ?? '';
  const lastName = (personalInfo['AssesseeName'] as Record<string, unknown> | undefined)?.['SurNameOrOrgName'] ?? '';
  const pan = (personalInfo['PAN'] as string) ?? '';
  const dob = (personalInfo['DOB'] as string) ?? '';
  const ay = (form['AssessmentYear'] as string) ?? '2025-26';

  const scheduleAl: ItrScheduleAlRow[] = [];
  const al = form['ScheduleAL'] as Record<string, unknown> | undefined;
  if (al && Array.isArray(al['Immovable'])) {
    for (const e of al['Immovable'] as Array<Record<string, unknown>>) {
      scheduleAl.push({
        category: 'immovable',
        description: (e['Description'] as string) ?? '',
        costPaise: Math.round(((e['Cost'] as number) ?? 0) * 100),
      });
    }
  }
  if (al && Array.isArray(al['Movable'])) {
    for (const e of al['Movable'] as Array<Record<string, unknown>>) {
      const desc = (e['Description'] as string) ?? '';
      const cat: ItrScheduleAlRow['category'] =
        /bank|deposit/i.test(desc) ? 'bank' :
        /share|equity|demat/i.test(desc) ? 'shares' :
        /insurance|policy/i.test(desc) ? 'insurance' :
        /gold|jewell/i.test(desc) ? 'jewellery' :
        /vehicle|car|bike/i.test(desc) ? 'vehicle' :
        /cash/i.test(desc) ? 'cash' : 'movable';
      scheduleAl.push({
        category: cat,
        description: desc,
        costPaise: Math.round(((e['Cost'] as number) ?? 0) * 100),
      });
    }
  }

  const taxPaid: ItrTaxPaid = {
    tdsPaise: Math.round((((form['TaxPaid'] as Record<string, unknown> | undefined)?.['TDS'] as number) ?? 0) * 100),
    advanceTaxPaise: Math.round((((form['TaxPaid'] as Record<string, unknown> | undefined)?.['AdvanceTax'] as number) ?? 0) * 100),
    selfAssessmentTaxPaise: Math.round((((form['TaxPaid'] as Record<string, unknown> | undefined)?.['SelfAssessmentTax'] as number) ?? 0) * 100),
    refundClaimedPaise: Math.round((((form['Refund'] as Record<string, unknown> | undefined)?.['RefundDue'] as number) ?? 0) * 100),
  };

  return {
    identity: {
      nameFull: `${name} ${lastName}`.trim(),
      pan,
      dob,
      assessmentYear: ay,
      formCode,
      residentialStatus: (personalInfo['ResidentialStatus'] as string) ?? '',
    },
    scheduleAl,
    capitalGains: [],
    taxPaid,
    grossTotalIncomePaise: Math.round((((form['PartB-TI'] as Record<string, unknown> | undefined)?.['GrossTotalIncome'] as number) ?? 0) * 100),
    fetchedAt: nowIso(),
  };
}

export function makeItrImporter(mode: ConnectorMode): ItrImporterAdapter {
  return new ItrImporterAdapter(mode);
}
